import React, { useState } from "react";
import InputField from "@/app/components/input";
import { RadioGroup } from "../../radio-button";
import CalendarInput from "../../calendar-input";
import CalendarIcon from "../../svg/calendar";
import Button from "../../button";
import ArrowRight from "../../svg/arrow-right";
import Pin from "../../svg/pin";
import FloorIcon from "../../svg/floor-icon";
import Square from "../../svg/square";

interface Step1Props {
    onNext: (data: any) => void;
    initialData: any;
    lang?: string;
    id?: string;
}

const Step1: React.FC<Step1Props> = ({ onNext, initialData, lang, id }) => {

    const accept = lang === 'en' ? 'Yes' : 'Ja';
    const notAccept = lang === 'en' ? 'No' : 'Nej';

    const [data, setData] = useState({
        moveType: initialData.moveType || "",
        date: initialData.date || null,
        movingFrom: initialData.movingFrom || "",
        squareMetersFrom: initialData.squareMetersFrom || "",
        floorFrom: initialData.floorFrom || "",
        elevatorFrom: initialData.elevatorFrom || notAccept,
        cleaningservice: initialData.cleaningservice || notAccept,
        packingservice: initialData.packingservice || notAccept,
        warehousing: initialData.warehousing || notAccept,
        things: initialData.things || notAccept,
    });

    const [errors, setErrors] = useState({
        moveType: false,
        date: false,
        movingFrom: false,
        squareMetersFrom: false,
        floorFrom: false,
        elevatorFrom: false,
        cleaningservice: false,
        packingservice: false,
        warehousing: false,
        things: false,
    });

    const handleNext = () => {
        const newErrors = {
            moveType: !data.moveType,
            date: !data.date,
            movingFrom: !data.movingFrom.trim(),
            squareMetersFrom: !data.squareMetersFrom.trim(),
            floorFrom: !data.floorFrom.trim(),
            elevatorFrom: !data.elevatorFrom,
            cleaningservice: !data.cleaningservice,
            packingservice: !data.packingservice,
            warehousing: !data.warehousing,
            things: !data.things,
        };

        setErrors(newErrors);

        if (!Object.values(newErrors).some((error) => error)) {
            onNext(data);
        }
    };

    const requredText = lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt';

    return (
        <div id={id} className="rounded-[40px] p-[48px] shadow-[0_10px_24px_0_#00000026] max-[576px]:p-[20px] max-[576px]:rounded-[20px]">
            <h2 className="text-[32px] font-bold text-violet-500 mb-[40px] max-[576px]:text-[20px] max-[576px]:leading-[24px] max-[576px]:mb-[24px] max-[576px]:text-[20px]">   
                {lang === 'en' ? 'Please fill in the required information' : ' Vänligen fyll i nödvändig information'}
            </h2>
            <div className="space-y-[32px]">
                <div className="space-y-[8px]">
                    <h3 className="font-medium text-sm text-violet-500 leading-[143%] uppercase max-[576px]:text-[12px] max-[576px]:leading-[12px]">
                        {lang === 'en' ? 'Elevator?' : 'Finns det någon hiss?' } 
                    </h3>
                    <RadioGroup
                        name="moveType"
                        options={[
                            { id: "private", label: lang === 'en' ?  "Private move" : "Privat flytt", value: lang === 'en' ?  "Private move" : "Privat flytt" },
                            {
                                id: "company",
                                label: lang === 'en' ?  "Company relocation" : "Företagsflytt",
                                value: "Company relocation",
                            },
                        ]}
                        selectedValue={data.moveType}
                        onChange={(value) => setData({ ...data, moveType: value })}
                    />
                    {errors.moveType && <span className="text-red-500 text-sm"> {requredText} </span>}
                </div>
                <div className="grid grid-cols-2 gap-6 max-[576px]:grid-cols-1">
                    <CalendarInput
                        label={lang === 'en' ? "Desired moving date" : "Önskat flyttdatum"}
                        value={data.date}
                        onChange={(date) => setData({ ...data, date })}
                        icon={<CalendarIcon />}
                        placeholder="YYYY/MM/DD"
                        errorMessage={errors.date ? lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'  : ""}
                    />
                </div>
                <div className="grid grid-cols-2 gap-6 max-[576px]:grid-cols-1">
                    <InputField
                        label={lang === 'en' ? "moving from" : "flyttar från"}
                        type="text"
                        iconLeft={<Pin />}
                        placeholder={lang === 'en' ? "Address" : "Adress"}
                        value={data.movingFrom}
                        onChange={(e) => setData({ ...data, movingFrom: e.target.value })}
                        errorMessage={errors.movingFrom ? lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'  : ""}
                    />
                    <InputField
                        label={lang === 'en' ? "number of square meters" : "antal kvadratmeter"}
                        type="number"
                        value={data.squareMetersFrom}
                        iconLeft={<Square />}
                        onChange={(e) =>
                            setData({ ...data, squareMetersFrom: e.target.value })
                        }
                        errorMessage={errors.squareMetersFrom ? lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'  : ""}
                    />
                </div>
                <div className="grid grid-cols-2 gap-6 max-[576px]:grid-cols-1">
                    <div className="mt-[28px] max-[576px]:mt-[0]">
                        <InputField
                            label={lang === 'en' ? "write the floor" : "Våning"}
                            placeholder={lang === 'en' ? "Please write the floor" : "Våning"}
                            iconLeft={<FloorIcon />}
                            type="number"
                            value={data.floorFrom}
                            onChange={(e) => setData({ ...data, floorFrom: e.target.value })}
                            errorMessage={errors.floorFrom ? lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'  : ""}
                        />
                    </div>
                    <div className="space-y-[8px]">
                        <h3 className="font-medium text-sm text-violet-500 leading-[143%] uppercase max-[576px]:text-[12px] max-[576px]:leading-[12px]">
                            {lang === 'en' ? 'Elevator?' : 'Finns hiss?' } 
                        </h3>
                        <RadioGroup
                            name="elevatorFrom"
                            mobileWidth="1/2"
                            options={[
                                { id: "yes", label: accept, value: accept },
                                { id: "no", label: notAccept, value: notAccept },
                            ]}
                            selectedValue={data.elevatorFrom}
                            onChange={(value) => setData({ ...data, elevatorFrom: value })}
                        />
                        {errors.elevatorFrom && <span className="text-red-500 text-sm">{requredText}</span>}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-6 max-[576px]:grid-cols-1">
                    <div className="space-y-[8px]">
                        <h3 className="font-medium text-sm text-violet-500 leading-[143%] uppercase max-[576px]:text-[12px] max-[576px]:leading-[12px]">      
                            {lang === 'en' ? 'DO YOU NEED CLEANING SERVICE?' : 'Behöver du flyttstäd?' }
                        </h3>
                        <RadioGroup
                            name="cleaningservice"
                            mobileWidth="1/2"
                            options={[
                                { id: "yes-cleaning", label: accept, value: accept },
                                { id: "no-cleaning", label: notAccept, value: notAccept },
                            ]}
                            selectedValue={data.cleaningservice}
                            onChange={(value) => setData({ ...data, cleaningservice: value })} 
                        />
                        {errors.cleaningservice && <span className="text-red-500 text-sm">{requredText}</span>}
                    </div>
                    <div className="space-y-[8px]">
                        <h3 className="font-medium text-sm text-violet-500 leading-[143%] uppercase max-[576px]:text-[12px] max-[576px]:leading-[12px]">
                            {lang === 'en' ? 'DO YOU NEED packing SERVICE?' : 'packning?' }
                        </h3>
                        <RadioGroup
                            name="packingservice"
                            mobileWidth="1/2"
                            options={[
                                { id: "yes-packing", label: accept, value: accept },
                                { id: "no-packing", label:notAccept, value:notAccept },
                            ]}
                            selectedValue={data.packingservice} 
                            onChange={(value) => setData({ ...data, packingservice: value })} 
                        />
                        {errors.packingservice && <span className="text-red-500 text-sm">{requredText}</span>}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-6 max-[576px]:grid-cols-1">
                    <div className="space-y-[8px]">
                        <h3 className="font-medium text-sm text-violet-500 leading-[143%] uppercase max-[576px]:text-[12px] max-[576px]:leading-[12px]">
                            {lang === 'en' ? 'DO YOU NEED warehousing SERVICE?' : 'Behöver du Magasinering ?' }
                        </h3>
                        <RadioGroup
                            name="warehousing"
                            mobileWidth="1/2"
                            options={[
                                { id: "yes-warehousing", label: accept, value: accept },
                                { id: "no-warehousing", label: notAccept, value: notAccept },
                            ]}
                            selectedValue={data.warehousing}
                            onChange={(value) => setData({ ...data, warehousing: value })} 
                        />
                        {errors.warehousing && <span className="text-red-500 text-sm">{requredText}</span>}
                    </div>
                    <div className="space-y-[8px]">
                        <h3 className="font-medium text-sm text-violet-500 leading-[143%] uppercase max-[576px]:text-[12px] max-[576px]:leading-[12px]">
                            {lang === 'en' ? 'are there any heavy things?' : 'tungvikt?' }
                        </h3>
                        <RadioGroup
                            name="things"
                            mobileWidth="1/2"
                            options={[
                                { id: "yes-things", label: accept, value: accept },
                                { id: "no-things", label: notAccept, value: notAccept },
                            ]}
                            selectedValue={data.things} 
                            onChange={(value) => setData({ ...data, things: value })} 
                        />
                        {errors.things && <span className="text-red-500 text-sm">{requredText}</span>}
                    </div>
                </div>
            </div>
            <div className="flex justify-end mt-[40px] max-[576px]:justify-center ">
                <Button
                    onClick={handleNext}
                    text={lang === 'en' ? 'continue' : 'fortsätta' }
                    icon={<ArrowRight />}
                    type="primary"
                    iconPostiton="right"
                    size="big"
                />
            </div>
        </div>
    );
};

export default Step1;